import { Grid, Skeleton } from '@mui/material';
import { HSpacer, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import styled from 'styled-components';

import ClaimSupervisorsAddImg from '../../../../assets/img/claims/supervisors/add_supervisor.svg';
import ApplicationImg from '../../../../assets/img/claims/supervisors/application.svg';
import ClaimSupervisorsCustomizeImg from '../../../../assets/img/claims/supervisors/customize.svg';
import ClaimSupervisorsInfoImg from '../../../../assets/img/claims/supervisors/info.svg';
import { HeaderContent } from '../../../../components/HeaderContent';
import { UnderLicenseClaim } from '../../../../components/new-layout/UnderLicenseClaim';
import { PLAN_TYPE } from '../../../../constants/Subscriptions';
import { Supervisor } from '../../../../domain/supervisor/Supervisor';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveSupervisorRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveSupervisorRightPanelInfo';
import { useRetrieveAllSupervisors } from '../../../../hooks/supervisor/useRetrieveAllSupervisors';
import { useSetSelectedSupervisors } from '../../../../hooks/supervisor/useSetSelectedSupervisors';
import { useSetSupervisorsColumns } from '../../../../hooks/supervisor/useSetSupervisorsColumns';
import { useRetrieveSupportDocs } from '../../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { DataTable } from '../../../../react-components/data-table/DataTable';
import { CreateSupervisorDialog } from './CreateSupervisorDialog';
import { SupervisorsActionsMenu } from './SupervisorsActionsMenu';
import { getSupervisorsColumns } from './SupervisorsListColumns';
import { SupervisorsListColumnsButton } from './SupervisorsListColumnsButton';
import { SupervisorRightPanel } from './supervisor-right-panel/SupervisorRightPanel';

const HeaderContainer = styled.div`
  display: relative;
  background-color: transparent;
  padding: 16px 22px;
  max-height: 117px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
`;

export const SupervisorsList = () => {
  const { data: user } = useRetrieveUser();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { detailType, setRightPanelInfo } = useRetrieveSupervisorRightPanelInfo();
  const [createDialog, setCreateDialog] = useState<boolean>(false);

  const [tableContextualAnchor, setTableContextualAnchor] = useState<
    { screenPosition: { top: number; left: number }; supervisor: Supervisor } | undefined
  >(undefined);

  const { data: organization } = useRetrieveOrganization();
  const { tableColumns } = useSetSupervisorsColumns();
  const { data: activeProject } = useRetrieveActiveProject();
  const { isLoading, data: supervisors } = useRetrieveAllSupervisors();
  const { data: selectedSupervisors, run: setSelectedSupervisors } = useSetSelectedSupervisors();

  if (!activeProject) return null;

  if (organization?.subscription?.plan === PLAN_TYPE.MONKEY) {
    return (
      <UnderLicenseClaim headerText={intl.formatMessage({ id: `supervisors.under_gorilla_claim.title` })}>
        <Grid container direction="column" alignItems="center">
          <VSpacer large />
          <VSpacer medium />

          <Grid
            item
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            style={{ maxWidth: `700px` }}
          >
            <img draggable="false" src={ApplicationImg} alt="" height="200" width="200" />
            <VSpacer large />
            <Text
              variant="body-medium"
              style={{ textAlign: `center`, fontSize: `13px` }}
              intlId="supervisors.under_gorilla_claim.header"
            />
          </Grid>

          <VSpacer large />

          <VSpacer large />

          <Grid item container direction="row" style={{ gap: `20px` }} alignItems="flex-start" justifyContent="center">
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              style={{ maxWidth: `300px` }}
            >
              <Grid item style={{ height: `120px` }}>
                <img draggable="false" src={ClaimSupervisorsAddImg} alt="" height="102" width="102" />
              </Grid>
              <VSpacer medium />
              <Grid item>
                <Text
                  style={{ textAlign: `center`, color: `#5073B5` }}
                  variant="h6"
                  intlId="supervisors.under_gorilla_claim.content.1"
                />
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              style={{ maxWidth: `300px` }}
            >
              <Grid item style={{ height: `120px` }}>
                <img draggable="false" src={ClaimSupervisorsCustomizeImg} alt="" height="102" width="102" />
              </Grid>
              <VSpacer medium />
              <Grid item>
                <Text
                  style={{ textAlign: `center`, color: `#5073B5` }}
                  variant="h6"
                  intlId="supervisors.under_gorilla_claim.content.2"
                />
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              style={{ maxWidth: `300px` }}
            >
              <Grid item style={{ height: `120px` }}>
                <img draggable="false" src={ClaimSupervisorsInfoImg} alt="" height="102" width="102" />
              </Grid>
              <VSpacer medium />
              <Grid item>
                <Text
                  style={{ textAlign: `center`, color: `#5073B5` }}
                  variant="h6"
                  intlId="supervisors.under_gorilla_claim.content.3"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </UnderLicenseClaim>
    );
  }

  return (
    <div
      style={{
        height: `100%`,
        width: `100%`,
        zIndex: 2,
        backgroundColor: `white`,
        display: `flex`,
        flexDirection: `row`,
        flexWrap: `nowrap`,
      }}
    >
      <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
        <HeaderContainer>
          <HeaderContent
            titleIntlId="supervisors.list.title"
            styles={{
              container: {
                backgroundColor: `transparent`,
              },
            }}
            article={{
              intlId: `supervisors.article`,
              link: retrieveDoc({ supportPage: `supervisors`, language: user?.language.value }),
            }}
          />

          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            <Button
              id="supervisors-create"
              key="supervisors-create"
              style={{
                width: `150px`,
              }}
              onClick={(e: any) => {
                setCreateDialog(true);
              }}
              disabled={isLoading}
              intlId="supervisors.list.create"
            />
          </Grid>
        </HeaderContainer>

        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{ padding: `22px`, height: `calc(100% - 117px)`, backgroundColor: `white` }}
        >
          <Grid container>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              wrap="nowrap"
              style={{
                minHeight: `46px`,
              }}
            >
              <Grid item container alignItems="center">
                {isLoading ? (
                  <Skeleton variant="rectangular" animation="pulse" height={20} width={220} />
                ) : (
                  <Text
                    variant="body"
                    intlId={`${
                      (selectedSupervisors ?? []).length > 0
                        ? `supervisors.header.selected_info`
                        : `supervisors.header.num_rows`
                    }`}
                    intlValues={{
                      num: (selectedSupervisors ?? []).length,
                      total: (supervisors ?? []).length,
                    }}
                  />
                )}

                <HSpacer small />

                {(selectedSupervisors ?? []).length > 0 ? (
                  <SupervisorsActionsMenu
                    type="popover"
                    supervisors={(supervisors ?? []).filter((doc) => selectedSupervisors.includes(doc.id))}
                    popoverProps={{
                      hideArrow: false,
                      containerStyle: {
                        justifyContent: `space-between`,
                      },
                      content: <Text variant="body" style={{ cursor: `pointer` }} intlId="supervisors.actions" />,
                    }}
                  />
                ) : null}
              </Grid>

              <Grid item container alignItems="center" justifyContent="flex-end">
                <SupervisorsListColumnsButton id="supervisors-configurable-table-columns" />
              </Grid>
            </Grid>
          </Grid>

          <Grid item container style={{ overflowX: `auto`, overflowY: `hidden`, height: `100%`, width: `100%` }}>
            <Grid container style={{ width: `100%`, height: `100%`, position: `relative` }}>
              <div style={{ height: `100%`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                <DataTable
                  loading={isLoading}
                  docs={(supervisors ?? []).map((supervisor: Supervisor) => {
                    const checkboxIcon = (
                      <div
                        style={{
                          width: `24px`,
                          height: `24px`,
                          borderRadius: `50%`,
                          backgroundColor: supervisor.color?.value,
                        }}
                      />
                    );
                    return {
                      doc: supervisor,
                      checkboxIcon: { element: checkboxIcon },
                    };
                  })}
                  total={(supervisors ?? []).length}
                  noFooter
                  onRowDoubleClick={(doc) => {
                    setRightPanelInfo({
                      detailType: `supervisor`,
                      detailId: `${doc.id}`,
                      detailTab: null,
                    });
                  }}
                  // hover
                  handleOnContextMenu={(ev: any, supervisor: Supervisor) => {
                    ev.preventDefault();
                    setTableContextualAnchor({
                      screenPosition: { top: ev.clientY, left: ev.clientX },
                      supervisor,
                    });
                  }}
                  selectedIds={selectedSupervisors}
                  onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
                    if (fromGlobalCheckbox) {
                      if (selectedSupervisors.length > 0) {
                        setSelectedSupervisors([]);
                      } else {
                        setSelectedSupervisors((supervisors ?? []).map((s) => s.id));
                      }
                    } else {
                      setSelectedSupervisors(selectedIds ?? []);
                    }
                  }}
                  // sortDirection={sortVehiclesInfo.order}
                  // sortBy={sortVehiclesInfo.orderBy}
                  // onColumnSort={(column: any, order: `asc` | `desc`) => {
                  //   console.info(`onColumnSort`, column, order);
                  //   setSortVehiclesInfo({ order, orderBy: column });
                  // }}
                  columns={[...getSupervisorsColumns({ tableColumns })]}
                />
              </div>
            </Grid>
          </Grid>

          {tableContextualAnchor ? (
            <SupervisorsActionsMenu
              type="contextual"
              contextAnchor={tableContextualAnchor}
              setContextAnchor={() => {
                setTableContextualAnchor(undefined);
              }}
              supervisors={[tableContextualAnchor.supervisor]}
            />
          ) : null}
        </Grid>

        {createDialog ? (
          <CreateSupervisorDialog
            onClose={() => {
              setCreateDialog(false);
            }}
          />
        ) : null}
      </Grid>

      {detailType === `supervisor` ? <SupervisorRightPanel /> : null}
    </div>
  );
};
